import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid, Stack, Switch } from '@mui/material';
import { createRef, useEffect, useState } from 'react';
import { ReactCropperElement } from 'react-cropper';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  IOurEntity,
  WorkspaceLogoDTO,
  ourEntityAllList,
  saveLogoDetails,
  uploadFileToS3,
} from 'wisestep-api';
import {
  WsFileUpload,
  FileTypeVariants,
  Label,
  WsSelectObj,
  WsTypography,
  WsTypoColors,
  WsButton,
} from 'wisestep-base';
import { object } from 'yup';

import { SettingsPageLayout } from '../../../components/SettingsPageLayout';
import WsCheckmarkSelect from '../../../components/WsCheckmarkSelect';
import { failed, success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { logoPayload } from '../helper/logoPayload';
import { UploadLogoDTO, workspaceSettingsIndexOptions } from '../helper/workspaceSettingsHelper';

import 'cropperjs/dist/cropper.css';
import LogoPreviewModal from './LogoPreviewModal';

const validationSchema = object().shape({});

export const logoPreviewContainerStyle = {
  borderRadius: 1.5,
  width: '100%',
  height: 'auto',
  maxWidth: { xs: '100%', sm: 400 },
  maxHeight: { xs: 233, sm: 300 },
  objectFit: 'contain',
  display: 'block',
  margin: 'auto',
};

const OrganizationLogoUpload = () => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const navigate = useNavigate();
  const [image, setImage] = useState('');
  const [selectedFile, setSelectedFile] = useState<File>();
  const [ourEntityList, setOurEntityList] = useState<WsSelectObj[]>();
  const [openLogoPreviewDialog, setOpenLogoPreviewDialog] = useState<boolean>(false);
  const [cropData, setCropData] = useState('#');
  const [isDefaultLogo, setIsDefaultLogo] = useState<boolean>(false);
  const cropperRef = createRef<ReactCropperElement>();
  const methods = useForm<UploadLogoDTO>({
    defaultValues: {},
    resolver: yupResolver(validationSchema),
  });

  const onUpload = async (files: File[]) => {
    setOpenLogoPreviewDialog(true);
    const reader = new FileReader();
    reader.onload = () => {
      setSelectedFile(files[0]);
      setImage(reader.result as any);
    };
    reader.readAsDataURL(files[0]);
  };

  const onSubmit = (data: UploadLogoDTO) => {
    const orgEntityIds: string[] = data.organizationEntityId.map((o) => o.value as string);
    if (
      typeof cropperRef.current?.cropper.getCroppedCanvas() === 'undefined' ||
      (orgEntityIds.length === 0 && !isDefaultLogo)
    ) {
      return;
    }
    cropperRef.current.cropper.getCroppedCanvas().toBlob(async (blob) => {
      const b: any = blob;
      b.lastModifiedDate = new Date();
      b.name = selectedFile?.name;
      try {
        const url = await uploadFileToS3(blob as File);
        data.logoUrl = url;
        if (url) {
          const workspaceLogoDTO: WorkspaceLogoDTO = { ...logoPayload };
          workspaceLogoDTO.logoType = 'LOGO';
          workspaceLogoDTO.organizationEntities = [...orgEntityIds];
          workspaceLogoDTO.s3Url = url;
          workspaceLogoDTO.workspaceDefault = isDefaultLogo;
          workspaceLogoDTO.markActive = true;
          await saveLogoDetails(workspaceLogoDTO);
          setNotification(
            success(
              !isDefaultLogo
                ? `Organization logo sucessfully uploaded.`
                : 'Default logo sucessfully uploaded'
            )
          );
          cropperRef.current?.cropper.clear();
          setImage('');
          navigate('/workspace-settings/organization-logo');
        }
      } catch (err) {
        setNotification(failed(`Error while uploading logo.`));
      }
    });
  };

  useEffect(() => {
    const getOurEntityList = async () => {
      const response = await ourEntityAllList();
      const optionsArray: WsSelectObj[] = [];
      response.data.data.forEach((obj: IOurEntity) => {
        optionsArray.push({ label: obj.Name, value: obj.id ?? '' });
      });
      setOurEntityList(optionsArray);
    };
    getOurEntityList();
  }, []);

  return (
    <>
      <SettingsPageLayout
        title="Upload Logo"
        indexOptions={workspaceSettingsIndexOptions}
        backUrl={'/workspace-settings/organization-logo'}
      >
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <Box borderRadius={1.5}>
              <Grid container py={1} alignItems="center">
                <Grid item p={2} sm={6}>
                  <WsCheckmarkSelect
                    name={'organizationEntityId'}
                    options={ourEntityList ?? []}
                    getLabel={(option) => option.label}
                  />
                </Grid>
                <Grid item pb={3} sm={2}>
                  <Label>
                    <WsTypography color={WsTypoColors.SECONDARY}>Logo:</WsTypography>
                  </Label>
                  <WsFileUpload
                    multiple={false}
                    onUpload={(file: File[]) => onUpload(file)}
                    name="logo_upload"
                    placeholderText=""
                    bgRequired={false}
                    buttonText={'Upload File'}
                    fileTypes={FileTypeVariants.IMAGES}
                  />
                </Grid>
              </Grid>
              {cropData && (
                <Grid container py={1} ml={1} mr={1}>
                  <Grid
                    item
                    p={1}
                    sm={12}
                    sx={{
                      border: '1px solid rgba(0, 0, 0, 0.12)',
                    }}
                    mr={2}
                    justifyContent="center"
                    alignContent="center"
                  >
                    <Box
                      component="img"
                      sx={logoPreviewContainerStyle}
                      src={cropData}
                      justifyContent="center"
                      alignItems="center"
                    />
                  </Grid>
                </Grid>
              )}
              <Stack p={1} spacing={2} direction="row" justifyContent="space-between">
                <Grid container alignItems="center">
                  <Grid item>
                    <Label>Default Logo:</Label>
                  </Grid>
                  <Grid item ml={1} pt={0.5}>
                    <Switch
                      name="isDefaultLogo"
                      onChange={() => {
                        setIsDefaultLogo(!isDefaultLogo);
                      }}
                      sx={{
                        transform: 'scale(1.5)',
                      }}
                    />
                  </Grid>
                </Grid>
                <Box display="flex" gap={2}>
                  <WsButton variant="outlined" size="medium" type="button">
                    {`Cancel`}
                  </WsButton>
                  <WsButton variant="contained" size="medium" type="submit">
                    {`Upload`}
                  </WsButton>
                </Box>
              </Stack>
            </Box>
          </form>
        </FormProvider>
        <LogoPreviewModal
          openDialog={openLogoPreviewDialog}
          handleClose={() => {
            setOpenLogoPreviewDialog(false);
          }}
          onSave={() => {
            if (typeof cropperRef.current?.cropper !== 'undefined') {
              setCropData(cropperRef.current?.cropper.getCroppedCanvas().toDataURL());
              setOpenLogoPreviewDialog(false);
            }
          }}
          cropperRef={cropperRef}
          image={image}
        />
      </SettingsPageLayout>
    </>
  );
};

export default OrganizationLogoUpload;
